import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import di from "../../../../dependency_injection";
import { isLeft } from "fp-ts/lib/Either";
import LocalizationContext from "../../../providers/localization/LocalizationContext";
import LocalizationContextType from "../../../../domain/providers/localization/LocalizationContextType";
import ModalsContext from "../../../providers/modal/ModalsContext";
import ModalsContextType from "../../../../domain/providers/modal/ModalsContextType";
import KeyWordLocalization from "../../../utils/KeyWordLocalization";
import SearchRankingHumanAgentsUseCase, { SearchRankingHumanAgentsUseCaseName } from "../../../../domain/use_cases/dashboard/search_ranking_human_agents.use_case";
import DownloadAgentResumeUseCase, { DownloadAgentResumeUseCaseName } from "../../../../domain/use_cases/human_agent/dowlonad_agent_resume.use_case";
import { HumanRanking } from "../../../../domain/repositories/dashboard.repository";
import SearchResultEntity from "../../../../domain/entities/search_result.entity";
import AgentAnalysisComponent from "../../dashboard/components/agent_analysis/agent_analysis.component";
import SearchAgentsUseCase, { SearchAgentsUseCaseName } from "../../../../domain/use_cases/qa/search_agents.use_case";
import { AutoCompleteItem } from "../../../components/autocompleteWithCheck/AutoCompleteWithCheckProps";
import QAAgentEntity from "../../../../domain/entities/qa_agent.entity";
import GetAgentByIdUseCase, { GetAgentByIdUseCaseName } from "../../../../domain/use_cases/qa/get_agent_by_id.use_case";

const useRankingAgentsPageViewModel = () => {
    const { i18n } = useContext(LocalizationContext) as LocalizationContextType;
    const { openModalCustom, addToast } = useContext(ModalsContext) as ModalsContextType;
    const formInfo = useForm();
    const { register, handleSubmit, setValue, watch, getValues, formState: { errors } } = formInfo;
    const [data, setData] = useState<SearchResultEntity<HumanRanking> | null | undefined>(null);
    const [loaded, setLoaded] = useState<boolean>(false);
    const colors = ['#ff645e', '#8c8484', '#73bf94'];
    const location = useLocation();
    const navigate = useNavigate();

    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);

    const _searchRankingData = async (params: {
        query: string;
        initDate?: Date;
        endDate?: Date;
        page: number;
        itemsPerPage: number;
        sortBy: "ASC" | "DESC";
        sortField?: "average_score" | "total_calls_evaluated" | "name"; 
        aiAgentId: string | undefined;
    }) => {
        _updateURL(params);
        setData(undefined);
        setLoaded(false);

        const mappedParams = {
            query: params.query,
            start_date: params.initDate
              ? params.initDate.toISOString().split("T")[0]
              : undefined,
            end_date: params.endDate
              ? params.endDate.toISOString().split("T")[0]
              : undefined,
            page_number: params.page,
            page_size: params.itemsPerPage,
            sort: params.sortBy,
            sort_by: params.sortField,
            ai_agent_id: params.aiAgentId,
          };

        const response = await di.get<SearchRankingHumanAgentsUseCase>(SearchRankingHumanAgentsUseCaseName).call(params);
        if (isLeft(response)) {
            setData(null);
            setLoaded(true);
            return;
        } else {
            setLoaded(true);
            setData(response.right);
        }
        const result = response.right;

        if (!result.data || result.data.length === 0) {
          addToast(i18n(KeyWordLocalization.RankingAgentsPageNoData), "warning", undefined);
        }
    };

    const _updateURL = (params: {
        query: string;
        initDate?: Date;
        endDate?: Date;
        page: number;
        sortBy: "ASC" | "DESC";
        sortField?: "average_score" | "total_calls_evaluated" | "name"; 
        aiAgentId: string | undefined;
        itemsPerPage: number;
    }) => {
        let url = "?";
        if (params.query) url += `query=${params.query}&`;
        if (params.initDate) url += `initDate=${params.initDate.toISOString().split('T')[0]}&`;
        if (params.endDate) url += `endDate=${params.endDate.toISOString().split('T')[0]}&`;
        if (params.page) url += `page=${params.page}&`;
          if (params.itemsPerPage) url += `page_size=${params.itemsPerPage}&`;
        if (params.sortBy) url += `sortBy=${params.sortBy}&`;
        if (params.sortField) url += `sortField=${params.sortField}&`;
        if (params.aiAgentId) url += `aiAgentId=${params.aiAgentId}&`;

        if (url.endsWith("&")) url = url.slice(0, -1);
        navigate({ pathname: location.pathname, search: url });
    };

    const _onSubmit = (data: any) => {
        let params = {
            query: data[KeyWordLocalization.RankingAgentsPageSearchByName],
            initDate: data[KeyWordLocalization.RankingAgentsPageStartDate] ? new Date(data[KeyWordLocalization.RankingAgentsPageStartDate]) : undefined,
            endDate: data[KeyWordLocalization.RankingAgentsPageEndDate] ? new Date(data[KeyWordLocalization.RankingAgentsPageEndDate]) : undefined,
            page: 1,
            itemsPerPage,
            sortBy: data[KeyWordLocalization.RankingAgentsPageSortBy],
            sortField: data[KeyWordLocalization.RankingAgentsPageSortField],
            aiAgentId: data[KeyWordLocalization.RankingAgentsPageAiAgentId] || undefined,
        };
        setValue(KeyWordLocalization.RankingAgentsPagePage, 1);
        _updateURL(params);
    };

    const _handleChangePage = (page: number) => {
        setValue(KeyWordLocalization.RankingAgentsPagePage, page);
        const data = getValues();
        const params = {
            query: data[KeyWordLocalization.RankingAgentsPageSearchByName],
            initDate: data[KeyWordLocalization.RankingAgentsPageStartDate] ? new Date(data[KeyWordLocalization.RankingAgentsPageStartDate]) : undefined,
            endDate: data[KeyWordLocalization.RankingAgentsPageEndDate] ? new Date(data[KeyWordLocalization.RankingAgentsPageEndDate]) : undefined,
            page: page,
            itemsPerPage,
            sortBy: data[KeyWordLocalization.RankingAgentsPageSortBy],
            aiAgentId: data[KeyWordLocalization.RankingAgentsPageAiAgentId] || undefined,
        };

        _searchRankingData(params);
        _updateURL(params);
    };

    const _handleChangeSortBy = (sortBy: "ASC" | "DESC") => {
        setValue(KeyWordLocalization.RankingAgentsPageSortBy, sortBy);
        const data = getValues();
        const params = {
            query: data[KeyWordLocalization.RankingAgentsPageSearchByName],
            initDate: data[KeyWordLocalization.RankingAgentsPageStartDate] ? new Date(data[KeyWordLocalization.RankingAgentsPageStartDate]) : undefined,
            endDate: data[KeyWordLocalization.RankingAgentsPageEndDate] ? new Date(data[KeyWordLocalization.RankingAgentsPageEndDate]) : undefined,
            page: data[KeyWordLocalization.RankingAgentsPagePage],
            itemsPerPage,
            sortBy: sortBy,
            aiAgentId: data[KeyWordLocalization.RankingAgentsPageAiAgentId] || undefined,
        };
        _updateURL(params);
    };


    const _handleChangeSortField = (field: "average_score" | "total_calls_evaluated" | "name") => {
        const currentField = getValues()[KeyWordLocalization.RankingAgentsPageSortField];
        const currentOrder = getValues()[KeyWordLocalization.RankingAgentsPageSortBy];

        const newOrder =
            currentField === field ? (currentOrder === "ASC" ? "DESC" : "ASC") : "ASC";

        setValue(KeyWordLocalization.RankingAgentsPageSortField, field);
        setValue(KeyWordLocalization.RankingAgentsPageSortBy, newOrder);

        const data = getValues();

        const params = {
            query: data[KeyWordLocalization.RankingAgentsPageSearchByName],
            initDate: data[KeyWordLocalization.RankingAgentsPageStartDate]
                ? new Date(data[KeyWordLocalization.RankingAgentsPageStartDate])
                : undefined,
            endDate: data[KeyWordLocalization.RankingAgentsPageEndDate]
                ? new Date(data[KeyWordLocalization.RankingAgentsPageEndDate])
                : undefined,
            page: 1,
            itemsPerPage,
            sortBy: newOrder as "ASC" | "DESC",            sortField: field,
            aiAgentId: data[KeyWordLocalization.RankingAgentsPageAiAgentId] || undefined,
        };

        setValue(KeyWordLocalization.RankingAgentsPagePage, 1);
        _searchRankingData(params);
        _updateURL(params);
    };


    const _handleSeeAgentDetails = (agentId: string, agentName: string) => {
        const data = getValues();
        const initDate = data[KeyWordLocalization.RankingAgentsPageStartDate] ? new Date(data[KeyWordLocalization.RankingAgentsPageStartDate]) : new Date();
        const endDate = data[KeyWordLocalization.RankingAgentsPageEndDate] ? new Date(data[KeyWordLocalization.RankingAgentsPageEndDate]) : new Date();
        openModalCustom("lg", i18n(KeyWordLocalization.TableSatisfactionDetailClient, { name: agentName }), <AgentAnalysisComponent agentId={agentId} endDate={endDate} startDate={initDate} />, {
            hideCardBody: true,
        });
    };

    const _handleDownloadAgentData = async (agentId: string) => {
        const data = getValues();
        const initDate = data[KeyWordLocalization.RankingAgentsPageStartDate] ? new Date(data[KeyWordLocalization.RankingAgentsPageStartDate]) : undefined;
        const endDate = data[KeyWordLocalization.RankingAgentsPageEndDate] ? new Date(data[KeyWordLocalization.RankingAgentsPageEndDate]) : undefined;
        const response = await di.get<DownloadAgentResumeUseCase>(DownloadAgentResumeUseCaseName).call({
            agentId,
            startDate: initDate,
            endDate,
        });
        if (isLeft(response)) {
            return addToast(response.left.code ?? KeyWordLocalization.UnknownError, "error", undefined);
        }
        addToast(i18n(KeyWordLocalization.RankingAgentsPageDownloadedSuccess), "success", undefined);
    };


    const onSearchAiAgent = async (query: string): Promise<AutoCompleteItem<QAAgentEntity>[]> => {
        console.log("query", getValues());
        const response = await di.get<SearchAgentsUseCase>(SearchAgentsUseCaseName).call({
            itemsPerPage: 20,
            page: 1,
            query,
        });
        if (isLeft(response)) return [];
        const parsed = response.right.data.map((item) => {
            return {
                label: item.name,
                id: item.id,
                aditionalValue: item,
            }
        });
        return parsed;
    };

    const handleChangeAiAgent = (item: AutoCompleteItem<QAAgentEntity>) => {
        setValue(KeyWordLocalization.RankingAgentsPageAiAgentId, item?.id);
        _onSubmit(getValues());
    };


    const _loadAiAgent = async () => {
        const queryParams = new URLSearchParams(location.search);
        const aiAgentQuery = queryParams.get("aiAgentId") || undefined;
        if (aiAgentQuery && getValues()[KeyWordLocalization.RankingAgentsPageFormForm] == "") {
            const response = await di.get<GetAgentByIdUseCase>(GetAgentByIdUseCaseName).call(aiAgentQuery);
            if (isLeft(response)) return;
            const parsed: AutoCompleteItem<QAAgentEntity> = {
                label: response.right.name,
                id: response.right.id,
                aditionalValue: response.right,
            };
            setValue(KeyWordLocalization.RankingAgentsPageFormForm, [parsed]);
        }
    }

    const onChangeForm = () => {
        const data = getValues();
        _onSubmit(data);
    }

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const query = queryParams.get("query") || "";
        const initDate = queryParams.get("initDate") ? new Date(queryParams.get("initDate")!) : undefined;
        const endDate = queryParams.get("endDate") ? new Date(queryParams.get("endDate")!) : undefined;
        const page = parseInt(queryParams.get("page") || "1", 10);
        const sortBy = (queryParams.get("sortBy") as "ASC" | "DESC") || "ASC";
        const aiAgentId = queryParams.get("aiAgentId") || undefined;
        const sortField = queryParams.get("sortField") || "average_score"; // valor por defecto

        setValue(KeyWordLocalization.RankingAgentsPageSortField, sortField);
        setValue(KeyWordLocalization.RankingAgentsPageSortBy, sortBy);
        setValue(KeyWordLocalization.RankingAgentsPageSearchByName, query);
        setValue(KeyWordLocalization.RankingAgentsPageStartDate, initDate ? initDate.toISOString().split("T")[0] : "");
        setValue(KeyWordLocalization.RankingAgentsPageEndDate, endDate ? endDate.toISOString().split("T")[0] : "");
        setValue(KeyWordLocalization.RankingAgentsPagePage, page);
        if (aiAgentId) setValue(KeyWordLocalization.RankingAgentsPageAiAgentId, aiAgentId);

        _loadAiAgent();

        const params = {
            query,
            initDate,
            endDate,
            page,
            itemsPerPage,
            sortBy,
            aiAgentId,
            sortField: sortField as "average_score" | "total_calls_evaluated" | "name",
        };
        _searchRankingData(params);
    }, [location.search]);




    return {
        i18n,
        register,
        handleSubmit,
        setValue,
        watch,
        getValues,
        errors,
        data,
        loaded,
        colors,
        _onSubmit,
        _handleChangePage,
        _handleChangeSortBy,
        _handleSeeAgentDetails,
        _handleDownloadAgentData,
        formInfo,
        onSearchAiAgent,
        handleChangeAiAgent,
        _handleChangeSortField,
        onChangeForm,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        setItemsPerPage,
        _searchRankingData,
        _updateURL,
    };
};

export default useRankingAgentsPageViewModel;